import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Loader2, Save, Trash2, TriangleAlert } from 'lucide-react';

import ChoixActivite from './ChoixActivite';
import EcouteReunion from './EcouteReunion';
import {
  assistantsService,
  type CompteRendu,
  type Projet,
  type Reunion,
} from '../../services/api/assistants';

interface Props {
  reunion: Reunion;
  projets: Projet[];
  onValidee: (reunion: Reunion) => void;
}

/**
 * Relecture d'un compte rendu avant son versement à la mémoire documentaire.
 *
 * C'est le point de contrôle prévu au contrat. Rien n'entre dans la mémoire de
 * l'activité tant qu'un agent n'a pas relu : à partir du versement, l'assistant
 * documentaire citera ce compte rendu comme il cite un document officiel, et
 * une erreur non corrigée se propagerait à toutes les productions ultérieures.
 *
 * La transcription est affichée à côté, et chaque élément renvoie au passage
 * qui le fonde. Sans ce renvoi, relire reviendrait à croire le modèle sur
 * parole — ce qui est exactement ce qu'on cherche à éviter.
 */
const RelectureReunion: React.FC<Props> = ({ reunion, projets, onValidee }) => {
  const [compteRendu, setCompteRendu] = useState<CompteRendu>(
    () => normaliserCompteRendu(reunion.compte_rendu),
  );
  const [extraitActif, setExtraitActif] = useState('');
  const [enregistrement, setEnregistrement] = useState(false);
  const [validation, setValidation] = useState(false);
  const [modifie, setModifie] = useState(false);
  const [erreur, setErreur] = useState('');
  // Activité choisie ici quand la réunion a été déposée sans elle.
  const [activite, setActivite] = useState<number | string | null>(null);
  const surligne = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setCompteRendu(normaliserCompteRendu(reunion.compte_rendu));
    setModifie(false);
    setExtraitActif('');
  }, [reunion.id, reunion.compte_rendu]);

  // Le passage désigné est amené sous les yeux : sur une transcription d'une
  // heure, laisser l'agent le chercher revient à ne pas l'avoir affiché.
  useEffect(() => {
    surligne.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [extraitActif]);

  const segments = useMemo(() => decouper(reunion), [reunion]);
  const situation = useMemo(
    () => situer(segments, extraitActif),
    [segments, extraitActif],
  );

  const modifier = (transformation: (courant: CompteRendu) => CompteRendu) => {
    setCompteRendu(transformation);
    setModifie(true);
    setErreur('');
  };

  const enregistrer = async () => {
    setEnregistrement(true);
    setErreur('');
    try {
      await assistantsService.corrigerReunion(reunion.id, compteRendu);
      setModifie(false);
    } catch (e: any) {
      setErreur(e.message || "Les corrections n'ont pas pu être enregistrées.");
    } finally {
      setEnregistrement(false);
    }
  };

  /**
   * Verse le compte rendu relu.
   *
   * Les corrections sont enregistrées d'abord : le serveur verse ce qu'il a en
   * base, et valider sans enregistrer verserait la version du modèle, pas celle
   * qui vient d'être relue.
   */
  const valider = async () => {
    const rattachement = typeof activite === 'string' ? activite.trim() : activite;
    if (!reunion.projet && !rattachement) {
      setErreur("Choisissez l'activité à laquelle verser ce compte rendu.");
      return;
    }
    setValidation(true);
    setErreur('');
    try {
      if (modifie) {
        await assistantsService.corrigerReunion(reunion.id, compteRendu);
        setModifie(false);
      }
      onValidee(await assistantsService.validerReunion(
        reunion.id, rattachement || undefined,
      ));
    } catch (e: any) {
      setErreur(e.message || "Le compte rendu n'a pas pu être versé.");
    } finally {
      setValidation(false);
    }
  };

  const dejaVersee = reunion.etat === 'validee';
  const aRanger = !dejaVersee && !reunion.projet;

  return (
    <div className="space-y-4">
      {dejaVersee ? (
        <div className="flex items-center gap-2 rounded-md border border-[#DDE2DD] bg-eg-voile
          px-4 py-3 text-sm text-eg-encre">
          <CheckCircle2 className="w-4 h-4 text-eg-accent shrink-0" />
          Ce compte rendu a été validé et versé aux documents de l'activité
          « {reunion.projet_nom} ». Il est désormais cité par l'assistant documentaire.
        </div>
      ) : (
        <div className="space-y-3 rounded-md border border-[#DDE2DD] bg-white px-4 py-3">
          {aRanger && (
            <div className="rounded-md bg-eg-voile px-3 py-3">
              <label className="text-xs font-semibold uppercase tracking-wide text-eg-gris">
                Activité à laquelle verser ce compte rendu
              </label>
              <div className="mt-1">
                <ChoixActivite
                  projets={projets}
                  valeur={activite}
                  onChange={setActivite}
                  desactive={validation}
                />
              </div>
              <p className="mt-1 text-xs text-eg-estompe">
                Cette réunion a été déposée sans activité. Le versement en demande une :
                c'est elle qui décide quelle mémoire le compte rendu vient alimenter.
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-eg-gris">
            Rien n'est versé à la mémoire de l'activité tant que vous n'avez pas validé.
            Cliquez sur un élément pour retrouver le passage qui le fonde.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={enregistrer}
              disabled={!modifie || enregistrement || validation}
              className="inline-flex items-center gap-2 rounded-md border border-[#DDE2DD]
                px-3 py-2 text-sm font-medium text-eg-encre disabled:opacity-40"
            >
              {enregistrement ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>
            <button
              type="button"
              onClick={valider}
              disabled={validation || enregistrement}
              className="inline-flex items-center gap-2 rounded-md bg-eg-ombre px-4 py-2
                text-sm font-medium text-white disabled:opacity-50"
            >
              {validation ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Valider et verser
            </button>
          </div>
          </div>
        </div>
      )}

      {erreur && (
        <div className="flex items-start gap-2 rounded-md border border-[#E4B9B9] bg-[#FBF1F1]
          px-4 py-3 text-sm text-[#8A2A2A]">
          <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" />
          {erreur}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="rounded-md border border-[#DDE2DD] bg-white p-4 space-y-5
          max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-eg-gris">
              Objet
            </label>
            <input
              value={compteRendu.titre}
              readOnly={dejaVersee}
              onChange={(e) => modifier((c) => ({ ...c, titre: e.target.value }))}
              className="mt-1 w-full rounded-md border border-[#DDE2DD] px-3 py-2 text-sm
                font-medium focus:border-eg-accent focus:outline-none read-only:bg-eg-voile"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-eg-gris">
              Ce qu'il faut retenir
            </label>
            <textarea
              value={compteRendu.resume}
              readOnly={dejaVersee}
              rows={4}
              onChange={(e) => modifier((c) => ({ ...c, resume: e.target.value }))}
              className="mt-1 w-full rounded-md border border-[#DDE2DD] px-3 py-2 text-sm
                focus:border-eg-accent focus:outline-none read-only:bg-eg-voile"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-eg-gris">
              Participants
            </label>
            <input
              value={compteRendu.participants.join(', ')}
              readOnly={dejaVersee}
              placeholder="Séparés par des virgules"
              onChange={(e) => modifier((c) => ({
                ...c,
                participants: e.target.value.split(',').map((p) => p.trim()).filter(Boolean),
              }))}
              className="mt-1 w-full rounded-md border border-[#DDE2DD] px-3 py-2 text-sm
                focus:border-eg-accent focus:outline-none read-only:bg-eg-voile"
            />
            <p className="mt-1 text-xs text-eg-estompe">
              La transcription automatique déforme les noms propres : vérifiez-les avant de valider.
            </p>
          </div>

          {(compteRendu.sections || []).length > 0 && (
            <Bloc titre="Selon votre plan" vide="">
              {(compteRendu.sections || []).map((section, rang) => (
                <Element
                  key={`section-${rang}`}
                  actif={!!section.extrait && extraitActif === section.extrait}
                  onSituer={() => section.extrait && setExtraitActif(section.extrait)}
                >
                  <div className="text-sm font-medium text-eg-encre">{section.titre}</div>
                  <textarea
                    value={section.contenu}
                    readOnly={dejaVersee}
                    rows={2}
                    onChange={(e) => modifier((c) => ({
                      ...c,
                      sections: (c.sections || []).map((s, i) => (
                        i === rang ? { ...s, contenu: e.target.value } : s
                      )),
                    }))}
                    className={`mt-1 w-full resize-y bg-transparent text-sm focus:outline-none ${
                      section.extrait ? 'text-eg-gris' : 'italic text-eg-estompe'
                    }`}
                  />
                </Element>
              ))}
            </Bloc>
          )}

          <Bloc titre="Points abordés" vide="Aucun point relevé.">
            {compteRendu.points.map((point, rang) => (
              <Element
                key={rang}
                actif={extraitActif === point.extrait}
                onSituer={() => setExtraitActif(point.extrait)}
                onRetirer={dejaVersee ? undefined : () => modifier((c) => ({
                  ...c, points: c.points.filter((_, i) => i !== rang),
                }))}
              >
                <input
                  value={point.sujet}
                  readOnly={dejaVersee}
                  onChange={(e) => modifier((c) => ({
                    ...c,
                    points: c.points.map((p, i) => (i === rang ? { ...p, sujet: e.target.value } : p)),
                  }))}
                  className="w-full bg-transparent text-sm font-medium text-eg-encre
                    focus:outline-none"
                />
                <textarea
                  value={point.teneur}
                  readOnly={dejaVersee}
                  rows={2}
                  onChange={(e) => modifier((c) => ({
                    ...c,
                    points: c.points.map((p, i) => (i === rang ? { ...p, teneur: e.target.value } : p)),
                  }))}
                  className="mt-1 w-full resize-y bg-transparent text-sm text-eg-gris
                    focus:outline-none"
                />
              </Element>
            ))}
          </Bloc>

          <Bloc titre="Décisions" vide="Aucune décision arrêtée.">
            {compteRendu.decisions.map((decision, rang) => (
              <Element
                key={rang}
                actif={extraitActif === decision.extrait}
                onSituer={() => setExtraitActif(decision.extrait)}
                onRetirer={dejaVersee ? undefined : () => modifier((c) => ({
                  ...c, decisions: c.decisions.filter((_, i) => i !== rang),
                }))}
              >
                <textarea
                  value={decision.decision}
                  readOnly={dejaVersee}
                  rows={2}
                  onChange={(e) => modifier((c) => ({
                    ...c,
                    decisions: c.decisions.map((d, i) => (
                      i === rang ? { ...d, decision: e.target.value } : d
                    )),
                  }))}
                  className="w-full resize-y bg-transparent text-sm text-eg-encre focus:outline-none"
                />
                <p className="mt-1 text-xs text-eg-estompe">Se rattache à : {decision.sujet}</p>
              </Element>
            ))}
          </Bloc>

          <Bloc titre="Actions" vide="Aucune action confiée.">
            {compteRendu.actions.map((action, rang) => (
              <Element
                key={rang}
                actif={extraitActif === action.extrait}
                onSituer={() => setExtraitActif(action.extrait)}
                onRetirer={dejaVersee ? undefined : () => modifier((c) => ({
                  ...c, actions: c.actions.filter((_, i) => i !== rang),
                }))}
              >
                <textarea
                  value={action.action}
                  readOnly={dejaVersee}
                  rows={2}
                  onChange={(e) => modifier((c) => ({
                    ...c,
                    actions: c.actions.map((a, i) => (
                      i === rang ? { ...a, action: e.target.value } : a
                    )),
                  }))}
                  className="w-full resize-y bg-transparent text-sm text-eg-encre focus:outline-none"
                />
                <div className="mt-1 flex flex-wrap gap-2">
                  <input
                    value={action.responsable}
                    readOnly={dejaVersee}
                    placeholder="Responsable"
                    onChange={(e) => modifier((c) => ({
                      ...c,
                      actions: c.actions.map((a, i) => (
                        i === rang ? { ...a, responsable: e.target.value } : a
                      )),
                    }))}
                    className="rounded border border-[#DDE2DD] px-2 py-1 text-xs
                      focus:border-eg-accent focus:outline-none"
                  />
                  <input
                    value={action.echeance}
                    readOnly={dejaVersee}
                    placeholder="Échéance"
                    onChange={(e) => modifier((c) => ({
                      ...c,
                      actions: c.actions.map((a, i) => (
                        i === rang ? { ...a, echeance: e.target.value } : a
                      )),
                    }))}
                    className="rounded border border-[#DDE2DD] px-2 py-1 text-xs
                      focus:border-eg-accent focus:outline-none"
                  />
                </div>
              </Element>
            ))}
          </Bloc>
        </section>

        <section className="rounded-md border border-[#DDE2DD] bg-eg-voile p-4
          max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-eg-gris">
              Transcription
            </h3>
            {reunion.moteur && (
              <span className="text-xs text-eg-estompe">Transcrite par {reunion.moteur}</span>
            )}
          </div>

          {reunion.a_enregistrement && <EcouteReunion reunionId={reunion.id} />}

          {!reunion.transcription ? (
            <p className="mt-3 text-sm text-eg-gris">La transcription n'est pas disponible.</p>
          ) : (
            <div className="mt-3 space-y-3">
              {extraitActif && !situation && (
                <p className="text-xs text-[#8A2A2A]">
                  Ce passage n'a pas été retrouvé tel quel dans la transcription.
                </p>
              )}
              {segments.map((segment, rang) => (
                <p key={rang} className="text-sm leading-relaxed text-eg-encre">
                  {segment.locuteur && (
                    <span className="mr-2 font-semibold text-eg-ombre">{segment.locuteur} :</span>
                  )}
                  {rendreSegment(segment.texte, situation, rang, surligne)}
                </p>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

/** Groupe de la colonne de gauche, avec son intitulé et son cas vide. */
const Bloc: React.FC<{ titre: string; vide: string; children: React.ReactNode }> = ({
  titre, vide, children,
}) => {
  const rempli = React.Children.count(children) > 0;
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-eg-gris">{titre}</h3>
      {rempli ? (
        <div className="mt-2 space-y-2">{children}</div>
      ) : (
        <p className="mt-2 text-sm text-eg-estompe">{vide}</p>
      )}
    </div>
  );
};

/** Élément du compte rendu : cliquer dessus amène le passage qui le fonde. */
const Element: React.FC<{
  actif: boolean;
  onSituer: () => void;
  onRetirer?: () => void;
  children: React.ReactNode;
}> = ({ actif, onSituer, onRetirer, children }) => (
  <div
    onClick={onSituer}
    className={`group cursor-pointer rounded-md border p-3 transition-colors ${
      actif ? 'border-eg-accent bg-eg-voile' : 'border-[#E9ECE9] hover:border-eg-accent'
    }`}
  >
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">{children}</div>
      {onRetirer && (
        <button
          type="button"
          title="Retirer cet élément du compte rendu"
          onClick={(e) => { e.stopPropagation(); onRetirer(); }}
          className="shrink-0 rounded p-1 text-eg-estompe opacity-0 transition-opacity
            hover:text-[#8A2A2A] group-hover:opacity-100"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  </div>
);

interface Segment {
  locuteur: string;
  texte: string;
}

/** Position d'un extrait : segment et bornes de caractères à l'intérieur. */
interface Situation {
  debutSegment: number;
  debutOffset: number;
  finSegment: number;
  finOffset: number;
}

/**
 * Découpe la transcription en tours de parole quand ils sont connus.
 *
 * Le moteur retenu ne rend pas toujours les intervenants ; on retombe alors sur
 * le texte suivi, découpé en paragraphes pour rester lisible.
 */
function decouper(reunion: Reunion): Segment[] {
  if (reunion.tours?.length) {
    return reunion.tours.map((tour) => ({ locuteur: tour.locuteur || '', texte: tour.texte }));
  }
  const texte = reunion.transcription || '';
  return texte
    .split(/\n{2,}|\n/)
    .map((paragraphe) => paragraphe.trim())
    .filter(Boolean)
    .map((paragraphe) => ({ locuteur: '', texte: paragraphe }));
}

/**
 * Réduit un texte à sa substance, comme le fait le contrôle côté serveur.
 *
 * L'extrait est recopié par le modèle à partir de la transcription, mais la
 * ponctuation et les majuscules ne survivent pas toujours au passage. Comparer
 * sur cette forme réduite retrouve le passage là où une égalité stricte le
 * manquerait.
 */
function reduire(texte: string): { reduit: string; positions: number[] } {
  let reduit = '';
  const positions: number[] = [];
  let espaceEnAttente = false;

  // La décomposition se fait caractère par caractère, et non sur le texte
  // entier : « é » devient deux caractères une fois décomposé, et les positions
  // relevées sur la forme décomposée ne désigneraient plus rien dans le texte
  // qu'on affiche.
  for (let i = 0; i < texte.length; i += 1) {
    const decompose = texte[i]
      .normalize('NFKD')
      .toLowerCase()
      .replace(/[̀-ͯ]/g, ''); // signes diacritiques

    let retenu = false;
    for (let j = 0; j < decompose.length; j += 1) {
      if (!/[a-z0-9]/.test(decompose[j])) continue;
      if (espaceEnAttente && reduit) {
        reduit += ' ';
        positions.push(i);
      }
      espaceEnAttente = false;
      reduit += decompose[j];
      positions.push(i);
      retenu = true;
    }
    if (!retenu) espaceEnAttente = true;
  }
  return { reduit, positions };
}

/**
 * Situe un extrait dans les segments affichés.
 *
 * La recherche porte sur la concaténation des segments : un passage cité peut
 * chevaucher deux tours de parole, et le refuser dans ce cas priverait de repère
 * les éléments les plus intéressants du compte rendu — ceux où quelqu'un
 * répond à quelqu'un d'autre.
 */
function situer(segments: Segment[], extrait: string): Situation | null {
  if (!extrait) return null;

  const cible = reduire(extrait).reduit;
  if (!cible) return null;

  let concatene = '';
  // Pour chaque caractère de la forme réduite : son segment et sa position
  // dans le texte d'origine de ce segment.
  const reperes: { segment: number; position: number }[] = [];

  segments.forEach((segment, rang) => {
    const { reduit, positions } = reduire(segment.texte);
    if (!reduit) return;
    if (concatene) {
      concatene += ' ';
      reperes.push({ segment: rang, position: 0 });
    }
    concatene += reduit;
    positions.forEach((position) => reperes.push({ segment: rang, position }));
  });

  const debut = concatene.indexOf(cible);
  if (debut === -1) return null;
  const fin = debut + cible.length - 1;

  return {
    debutSegment: reperes[debut].segment,
    debutOffset: reperes[debut].position,
    finSegment: reperes[fin].segment,
    finOffset: reperes[fin].position + 1,
  };
}

/** Rend un segment en soulignant la part couverte par l'extrait désigné. */
function rendreSegment(
  texte: string,
  situation: Situation | null,
  rang: number,
  reference: React.MutableRefObject<HTMLElement | null>,
) {
  if (!situation || rang < situation.debutSegment || rang > situation.finSegment) return texte;

  const debut = rang === situation.debutSegment ? situation.debutOffset : 0;
  const fin = rang === situation.finSegment ? situation.finOffset : texte.length;

  return (
    <>
      {texte.slice(0, debut)}
      <mark
        ref={rang === situation.debutSegment ? (reference as any) : undefined}
        className="rounded bg-[#FBEAB4] px-0.5"
      >
        {texte.slice(debut, fin)}
      </mark>
      {texte.slice(fin)}
    </>
  );
}

/** Complète un compte rendu partiel : l'écran ne doit pas dépendre du modèle. */
function normaliserCompteRendu(brut: Reunion['compte_rendu']): CompteRendu {
  const source = (brut || {}) as Partial<CompteRendu>;
  return {
    titre: source.titre || '',
    resume: source.resume || '',
    participants: source.participants || [],
    points: source.points || [],
    decisions: source.decisions || [],
    actions: source.actions || [],
    sections: source.sections || [],
  };
}

export default RelectureReunion;
